/**
 * Functions to extract information from Game model
 */

export const getTotalPoints = game => game.boxscore.pts;
export const getTotalPointsAllowed = game => game.opp_boxscore.pts;
export const getTotalPlusMinusPoints = game => game.boxscore.plus_minus;
export const getTotalPossessions = game => game.counts.possessions;
export const getTotalMinutes = game => game.boxscore.minutes;
export const getTotalShifts = game => game.counts.num_shifts;
export const getPointsPerPossessions = game => {
  if (game.counts.possessions === 0) return 0;
  return game.boxscore.pts / game.counts.possessions;
};
export const getPlusMinusPointsPerPossessions = game => {
  if (game.counts.possessions === 0) return 0;
  return game.boxscore.plus_minus / game.counts.possessions;
};
export const getTotalTimePlayed = game => game.boxscore.minutes; // game.boxscore.seconds

export const getPointsPer100Possessions = game =>
  (getTotalPoints(game) / getTotalPossessions(game)) * 100;
export const getPointsAllowedPer100Possessions = game =>
  (getTotalPointsAllowed(game) / getTotalPossessions(game)) * 100;
export const getNetPer100Possessions = game =>
  getPointsPer100Possessions(game) - getPointsAllowedPer100Possessions(game);

export const getPointsPer36Minutes = game =>
  (getTotalPoints(game) / getTotalMinutes(game)) * 36;
export const getPointsAllowedPer36Minutes = game =>
  (getTotalPointsAllowed(game) / getTotalMinutes(game)) * 36;
export const getNetPer36Minutes = game =>
  getPointsPer36Minutes(game) - getPointsAllowedPer36Minutes(game);

export const getFactor = (game, factorName) => game.calculated[factorName];
export const getOppFactor = (game, factorName) =>
  game.opp_calculated[factorName];
export const getNetFactor = (game, factorName) =>
  getFactor(game, factorName) - getOppFactor(game, factorName);
export const getTOVFactor = game =>
  getOppFactor(game, 'tov') - getFactor(game, 'tov');

export const getNetEFG = game => getNetFactor(game, 'efg');
export const getNetFTR = game => getNetFactor(game, 'ftr');
export const getNetTOV = game => getTOVFactor(game);
export const getNetORR = game => getNetFactor(game, 'orr');

export const getPointsPerMinute = game =>
  game.boxscore.pts / game.boxscore.minutes;
export const getPlusMinusPointsPerMinute = game =>
  game.boxscore.plus_minus / game.boxscore.minutes;

export const getTeamPointsScored = game => game.boxscore.pts;
export const getOpponentsPointsScored = game => game.opp_boxscore.pts;

// get an average value (the result of one function of above divided by game count)
export const average = fn => game => fn(game) / game.counts.num_games;

export const getEfg = totals => totals.calculated.efg;
export const getOpponentEfg = totals => totals.opp_calculated.efg;
export const getNetEfg = totals => getEfg(totals) - getOpponentEfg(totals);
export const getOrr = totals => totals.calculated.orr;
export const getOpponentOrr = totals => totals.opp_calculated.orr;
export const getNetOrr = totals => getOrr(totals) - getOpponentOrr(totals);
export const getFtr = totals => totals.calculated.ftr;
export const getOpponentFtr = totals => totals.opp_calculated.ftr;
export const getNetFtr = totals => getFtr(totals) - getOpponentFtr(totals);
export const getTov = totals => totals.calculated.tov;
export const getOpponentTov = totals => totals.opp_calculated.tov;
export const getNetTov = totals => getOpponentTov(totals) - getTov(totals);

// rim
export const getAtr = totals => totals.calculated.atr_percentage;
export const getOpponentAtr = totals => totals.opp_calculated.atr_percentage;
export const getNetAtr = totals => getAtr(totals) - getOpponentAtr(totals);
export const getAtrRate = totals => totals.calculated.atr_rate;
export const getOpponentAtrRate = totals => totals.opp_calculated.atr_rate;
export const getNetAtrRate = totals =>
  getAtrRate(totals) - getOpponentAtrRate(totals);

/*
 * EXAMPLE:
    {
      "calculated": {
        "fg_perc": 0.0,
        "fg3_perc": 0.0,
        "fg2_perc": 0.0,
        "ft_perc": 0.0,
        "fg3_par": 0.5,
        "pps": 0.0,
        "true_shooting": 0.0,
        "efg": 0.0,
        "orr": 0.0,
        "ftr": 0.0,
        "tov": 0.0,
        "possessions_per_game": 1.8624,
        "points_per_possession": 0.0
      },
      "boxscore": {
        "minutes": 1.18,
        "seconds": 71.0,
        "fgm": 0.0,
        "fga": 2.0,
        "fgm3": 0.0,
        "fga3": 1.0,
        "ft": 0.0,
        "fta": 0.0,
        "pts": 0.0,
        "rebso": 0.0,
        "rebsd": 0.0,
        "rebs": 0.0,
        "ast": 0.0,
        "to": 0.0,
        "stl": 0.0,
        "blk": 0.0,
        "fouls": 1.0,
        "plus_minus": -4.0,
        "opp_pts": 4.0
      },
      "counts": {
        "num_shifts": 1,
        "num_games": 1,
        "possessions": 1.92
      },
      "lineup": {
        "players": [
          {
            "name": "Mitchell, Shelton",
            "jersey": 4,
            "id": 1256
          },
          {
            "name": "Newman III, John",
            "jersey": 15,
            "id": 1257
          },
          {
            "name": "Simms, Aamir",
            "jersey": 25,
            "id": 1260
          },
          {
            "name": "Trapp, Clyde",
            "jersey": 0,
            "id": 1263
          },
          {
            "name": "White, Javan",
            "jersey": 35,
            "id": 1265
          }
        ],
        "id": 1009
      }
    },
 */
